import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Filter, RefreshCw, Mail, X, Loader2, Download, FileSpreadsheet, ClipboardList, FileText, Camera, Receipt, PenSquare, Send, Pencil } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { StatusBadge, GateBadge } from "../components/StatusBadge";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { downloadCsv, downloadXlsx, downloadGateGuidePdf, downloadGateWaybillPdf } from "../lib/exporters";
import AddAsPartnerButton from "../components/AddAsPartnerButton";
import { useAuth } from "../contexts/AuthContext";
import { GateEventForm } from "../components/GateEventFab";
import CorrectCarrierDialog from "../components/CorrectCarrierDialog";
import EditGateEventDialog from "../components/EditGateEventDialog";
import CorrectContainerNumberDialog from "../components/CorrectContainerNumberDialog";

const EVENT_EXPORT_COLS = [
  { key: "event_time", label: "Event Time", format: (v) => v ? new Date(v).toISOString() : "" },
  { key: "container_number", label: "Container" },
  { key: "container_iso_type", label: "ISO" },
  { key: "gate_type", label: "Gate" },
  { key: "is_full", label: "Load", format: (v) => v ? "Full" : "Empty" },
  { key: "location_code", label: "Location" },
  { key: "last_carrier", label: "Carrier" },
  { key: "carrier_code", label: "Carrier Code" },
  { key: "grade", label: "Grade" },
  { key: "condition", label: "Condition" },
  { key: "repair_status", label: "Repair Status" },
  { key: "surveyor_name", label: "Surveyor" },
  { key: "customer", label: "Customer" },
  { key: "contact_number", label: "Contact number" },
  { key: "truck_plate", label: "Truck" },
  { key: "driver_name", label: "Driver" },
  { key: "driver_license", label: "Driver license" },
  { key: "vessel_name", label: "Vessel" },
  { key: "seal_number", label: "Seal" },
  { key: "booking_number", label: "Booking #" },
  { key: "bill_of_lading", label: "B/L" },
  { key: "status", label: "Status" },
  { key: "services_performed", label: "Services", format: (v) => Array.isArray(v) ? v.join(" | ") : "" },
  { key: "notes", label: "Notes" },
];

const STATUSES = ["VALIDATED", "EDI_SENT", "EDI_FAILED", "CANCELLED"];


function EdiDialog({ event, onClose, onSent }) {
  const [recipient, setRecipient] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  if (!event) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/edi/generate", {
        gate_event_id: event.id,
        recipient_email: recipient,
      });
      if (data.status === "SENT") {
        toast.success("EDI generated and sent");
      } else {
        toast.warning(`EDI generated. Send failed: ${data.last_error || "see EDI page"}`);
      }
      onSent(data);
      onClose();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="edi-dialog">
      <div className="bg-white border border-gray-200 w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Generate EDI</div>
            <div className="font-display font-extrabold text-lg">CODECO 95B</div>
          </div>
          <button onClick={onClose} className="ps-btn ps-btn-secondary"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div className="border border-gray-200 bg-gray-50 p-3 text-xs">
            <div className="font-mono font-semibold text-sm">{event.container_number}</div>
            <div className="text-gray-500">Gate {event.gate_type} · {new Date(event.event_time).toLocaleString()}</div>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">
              Recipient email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              required
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="edi-gateway@partner.com"
              className="ps-input font-mono"
              data-testid="edi-recipient-input"
            />
          </div>
          {err && (
            <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 rounded-sm" data-testid="edi-dialog-error">{err}</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="ps-btn ps-btn-secondary">Cancel</button>
            <button type="submit" disabled={submitting} className="ps-btn ps-btn-primary" data-testid="edi-generate-submit">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Generate & send"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PhotosDialog({ event, onClose }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!event) { setPhotos([]); return; }
    setLoading(true);
    api.get(`/gate-events/${event.id}/photos`)
      .then((r) => setPhotos(r.data || []))
      .catch(() => setPhotos([]))
      .finally(() => setLoading(false));
  }, [event]);
  if (!event) return null;
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid="photos-dialog"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Photos</div>
            <div className="font-display font-extrabold text-lg">{event.container_number}</div>
            <div className="text-gray-500 text-sm">Gate {event.gate_type} · {new Date(event.event_time).toLocaleString()}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="photos-dialog-close"><X className="w-5 h-5" /></button>
        </div>
        {loading ? (
          <div className="text-center text-gray-400 py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : photos.length === 0 ? (
          <div className="text-center text-gray-400 py-10 text-sm">No photos attached to this event.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {photos.map((p) => (
              <div
                key={p.id}
                className={"relative rounded-md border-2 p-2 bg-white " + (p.is_damage ? "border-red-500" : "border-gray-200")}
                data-testid={`photo-${p.id}`}
              >
                <a href={`${apiBase}/api/gate-events/${event.id}/photos/${p.id}/file`} target="_blank" rel="noreferrer" className="block">
                  <img src={`${apiBase}/api/gate-events/${event.id}/photos/${p.id}/file`} alt={p.caption || p.filename} className="w-full h-32 object-cover rounded-sm bg-gray-100" />
                </a>
                {p.is_damage && (
                  <span className="absolute top-1 left-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">Damage</span>
                )}
                {p.caption && <div className="mt-2 text-[11px] text-gray-700 line-clamp-2">{p.caption}</div>}
                <div className="mt-1 text-[10px] text-gray-400">{p.uploaded_by_name}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



export default function GateEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [ediEvent, setEdiEvent] = useState(null);
  const [photosEvent, setPhotosEvent] = useState(null);
  const [correctEvent, setCorrectEvent] = useState(null);
  const [correctCnEvent, setCorrectCnEvent] = useState(null);
  const [editEvent, setEditEvent] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [partnerCodes, setPartnerCodes] = useState(new Set());
  const { user } = useAuth();
  const canEditPartners = user?.role === "admin" || user?.role === "supervisor";
  const canCorrectCarrier = ["admin", "supervisor", "general_manager", "depot_manager"].includes(user?.role);
  const canEditEvent = ["admin", "supervisor", "general_manager", "depot_manager", "operator"].includes(user?.role);
  const canCancelEvent = ["admin", "supervisor", "general_manager", "depot_manager"].includes(user?.role);
  const canCorrectCn = ["admin", "supervisor", "general_manager", "depot_manager"].includes(user?.role);
  const [filters, setFilters] = useState({
    container_number: "",
    gate_type: "",
    status: "",
    customer: "",
    carrier_code: "",
    date_from: "",
    date_to: "",
  });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    api.get("/containers/carriers").then((r) => setCarriers(r.data)).catch(() => {});
    api.get("/partners?fields=code")
      .then((r) => setPartnerCodes(new Set((r.data || []).map((p) => p.code))))
      .catch(() => {});
  }, []);

  // `silent=true` skips the "Loading…" placeholder so the table doesn't flash
  // empty during background polls — the row list just quietly updates in-place.
  const load = useCallback(async ({ silent = false } = {}) => {
    const reqId = ++reqIdRef.current;
    if (!silent) setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (!v) return;
      if (k === "date_from") {
        params.append("date_from", new Date(v).toISOString());
      } else if (k === "date_to") {
        params.append("date_to", new Date(v + "T23:59:59").toISOString());
      } else {
        params.append(k, v);
      }
    });
    try {
      const { data } = await api.get(`/gate-events?${params.toString()}`);
      if (reqId === reqIdRef.current) {
        setEvents(data);
        setLastRefresh(new Date());
      }
    } catch (e) {
      if (!silent) toast.error(formatApiError(e));
    } finally {
      if (reqId === reqIdRef.current && !silent) setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  // Refresh when the global FAB records a new gate event
  useEffect(() => {
    const onRefresh = () => load();
    window.addEventListener("gate-events:refresh", onRefresh);
    return () => window.removeEventListener("gate-events:refresh", onRefresh);
  }, [load]);

  // Auto-refresh — polls every 30 s while the tab is visible AND the user has
  // the toggle on. Also refreshes immediately when the tab regains focus, so
  // coming back from another app / phone lock gives fresh data without waiting
  // for the next tick. Pausing while the tab is hidden keeps us off the API on
  // devices left in a pocket.
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const tick = () => {
      if (document.visibilityState === "visible") load({ silent: true });
    };
    const interval = setInterval(tick, 30_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") load({ silent: true });
    };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [autoRefresh, load]);

  const onSaved = () => load();

  // Fetch photo metadata + inline each image as a data URL so the client-side
  // PDF generator (jsPDF) can embed them without extra network plumbing.
  // Kept intentionally small (top 3 photos) — the Arkas sample only shows 3.
  const downloadWaybill = async (event) => {
    let photos = [];
    try {
      const { data: metas } = await api.get(`/gate-events/${event.id}/photos`);
      const top = (metas || []).slice(0, 3);
      photos = await Promise.all(top.map(async (m) => {
        try {
          const res = await api.get(`/gate-events/${event.id}/photos/${m.id}/file`, { responseType: "blob" });
          const dataUrl = await new Promise((resolve) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result);
            r.readAsDataURL(res.data);
          });
          return { dataUrl, caption: m.caption || m.filename || "" };
        } catch {
          return null;
        }
      }));
      photos = photos.filter(Boolean);
    } catch {
      // Non-fatal — waybill still renders without photos.
    }
    try {
      await downloadGateWaybillPdf(event, photos);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resendEdi = async (event) => {
    if (!window.confirm(`Re-send CODECO for ${event.container_number} to ${event.last_carrier || event.carrier_code}?\n\nThis creates a fresh EDI message with the current event data. Skip if the carrier hasn't complained.`)) return;
    try {
      const { data } = await api.post(`/gate-events/${event.id}/resend-edi`);
      if (data.status === "SENT") {
        toast.success(`EDI re-sent to ${data.carrier}`);
      } else if (data.status === "FAILED") {
        toast.error(`Re-send failed: ${data.last_error?.slice(0, 100) || "unknown"}`);
      } else {
        toast.info(`EDI status: ${data.status}`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const cancelEvent = async (event) => {
    const label = event.gate_type === "OUT"
      ? `reverse this Gate OUT (container returns to IN on the dashboard)`
      : `cancel this Gate IN (container disappears from the yard)`;
    if (!window.confirm(
      `Are you sure you want to ${label}?\n\n` +
      `Container: ${event.container_number}\n` +
      `Event: ${event.gate_type} · ${new Date(event.event_time).toLocaleString()}\n\n` +
      `The event will be marked CANCELLED (kept in history for audit) and the container's dashboard status will refresh immediately.`
    )) return;
    try {
      const { data } = await api.delete(`/gate-events/${event.id}`);
      const cascade = data?.cache_cascade;
      if (cascade?.action === "updated") {
        toast.success(`${event.container_number} → ${cascade.after.last_status} (dashboard refreshed)`);
      } else if (cascade?.action === "deleted") {
        toast.success(`${event.container_number} removed from yard (no remaining events)`);
      } else {
        toast.success("Event cancelled");
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="gate-events-page">
      <PageHeader
        title="Gate Activity"
        subtitle="Chronological log — one row per IN/OUT event. Each event creates an EIR and triggers a CODECO EDI message."
        testid="gate-events-header"
        actions={
          <>
            <div className="flex items-center gap-2 mr-1 text-[11px] text-gray-500" data-testid="auto-refresh-indicator">
              {lastRefresh && (
                <span className="font-mono hidden sm:inline" title={lastRefresh.toLocaleString()}>
                  Updated {lastRefresh.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </span>
              )}
              <label className="inline-flex items-center gap-1.5 cursor-pointer select-none" title="Auto-refresh every 30 seconds">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="w-3.5 h-3.5 accent-[#006E9E]"
                  data-testid="auto-refresh-toggle"
                />
                <span className="font-semibold text-gray-700">Auto</span>
                {autoRefresh && (
                  <span className="w-1.5 h-1.5 rounded-full bg-[#99D420] animate-pulse" aria-hidden="true" />
                )}
              </label>
            </div>
            <Link to="/eirs" className="ps-btn ps-btn-secondary" data-testid="open-eir-register">
              <ClipboardList className="w-4 h-4" /> EIR Register
            </Link>
            <button
              className="ps-btn ps-btn-secondary"
              onClick={downloadGateGuidePdf}
              data-testid="download-gate-guide"
              title="Print the one-page quick reference"
            >
              <FileText className="w-4 h-4" /> Gate Guide
            </button>
            <button className="ps-btn ps-btn-secondary" onClick={load} data-testid="refresh-events">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button
              className="ps-btn ps-btn-secondary"
              disabled={!events.length}
              onClick={() => downloadCsv(events, `gate_events_${new Date().toISOString().slice(0,10)}`, EVENT_EXPORT_COLS)}
              data-testid="events-export-csv"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
            <button
              className="ps-btn ps-btn-secondary"
              disabled={!events.length}
              onClick={() => downloadXlsx(events, `gate_events_${new Date().toISOString().slice(0,10)}`, EVENT_EXPORT_COLS, "Gate Events")}
              data-testid="events-export-xlsx"
            >
              <FileSpreadsheet className="w-4 h-4" /> XLSX
            </button>
            <button className="ps-btn ps-btn-primary" onClick={() => setShowForm(true)} data-testid="new-gate-event">
              <Plus className="w-4 h-4" /> New gate event
            </button>
          </>
        }
      />

      <div className="p-6 sm:p-8 space-y-4">
        {/* Filters */}
        <div className="border border-gray-200 bg-white p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3" data-testid="filters">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
            <input
              placeholder="Container number"
              value={filters.container_number}
              onChange={(e) => setFilters({ ...filters, container_number: e.target.value.toUpperCase() })}
              className="ps-input font-mono pl-9"
              data-testid="filter-container"
            />
          </div>
          <select
            value={filters.carrier_code}
            onChange={(e) => setFilters({ ...filters, carrier_code: e.target.value })}
            className="ps-input font-mono"
            data-testid="filter-carrier"
          >
            <option value="">All carriers</option>
            {carriers.map((c) => (
              <option key={c.carrier} value={c.carrier}>{c.carrier} ({c.count})</option>
            ))}
          </select>
          <select
            value={filters.gate_type}
            onChange={(e) => setFilters({ ...filters, gate_type: e.target.value })}
            className="ps-input"
            data-testid="filter-gate-type"
          >
            <option value="">All gate types</option>
            <option value="IN">Gate IN</option>
            <option value="OUT">Gate OUT</option>
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="ps-input"
            data-testid="filter-status"
          >
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="relative">
            <Filter className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
            <input
              placeholder="Customer"
              value={filters.customer}
              onChange={(e) => setFilters({ ...filters, customer: e.target.value })}
              className="ps-input pl-9"
              data-testid="filter-customer"
            />
          </div>
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            className="ps-input font-mono"
            placeholder="From"
            data-testid="filter-date-from"
            title="From date"
          />
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            className="ps-input font-mono"
            placeholder="To"
            data-testid="filter-date-to"
            title="To date"
          />
        </div>

        {/* Table */}
        <div className="border border-gray-200 bg-white overflow-x-auto md:block">
          <table className="ps-table ps-table-cards" data-testid="events-table">
            <thead>
              <tr>
                <th>Container</th>
                <th>Gate</th>
                <th>Load</th>
                <th>Event time</th>
                <th>Location</th>
                <th>Carrier</th>
                <th>Grade / Condition</th>
                <th>Customer</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="text-center text-gray-400 py-10">Loading…</td></tr>
              )}
              {!loading && events.length === 0 && (
                <tr><td colSpan={10} className="text-center text-gray-400 py-10">No events match the current filters.</td></tr>
              )}
              {!loading && events.map((e) => (
                <tr key={e.id} data-testid={`event-row-${e.id}`}>
                  <td data-label="Container" className="font-mono font-semibold text-gray-900">{e.container_number}</td>
                  <td data-label="Gate"><GateBadge type={e.gate_type} /></td>
                  <td data-label="Load"><span className="ps-pill ps-pill-neutral"><span className="dot" />{e.is_full ? "Full" : "Empty"}</span></td>
                  <td data-label="Event time" className="font-mono text-gray-700">{new Date(e.event_time).toLocaleString()}</td>
                  <td data-label="Location" className="font-mono">{e.location_code}</td>
                  <td data-label="Carrier" className="font-mono">{e.last_carrier || e.carrier_code || <span className="text-gray-400">—</span>}</td>
                  <td data-label="Grade">
                    {e.grade ? <span className="ps-pill ps-pill-neutral"><span className="dot" />{e.grade}</span> : null}{" "}
                    {e.condition === "DAMAGED" ? <span className="ps-pill ps-pill-error"><span className="dot" />Damaged</span>
                     : e.condition === "UNDER_SURVEY" ? <span className="ps-pill ps-pill-warning"><span className="dot" />Survey</span>
                     : e.condition === "SOUND" ? <span className="ps-pill ps-pill-success"><span className="dot" />Sound</span>
                     : null}
                  </td>
                  <td data-label="Customer">
                    {e.customer ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span>{e.customer}</span>
                        <AddAsPartnerButton
                          name={e.customer}
                          role="customer"
                          canEdit={canEditPartners}
                          knownCodes={partnerCodes}
                        />
                      </span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td data-label="Status"><StatusBadge status={e.status} /></td>
                  <td data-label="Actions" className="text-right mobile-row-full md:text-right">
                    <div className="inline-flex flex-wrap gap-1.5 justify-end">
                      <Link
                        to={`/eirs?container=${encodeURIComponent(e.container_number)}`}
                        className="ps-btn ps-btn-secondary h-8 px-3 text-xs"
                        data-testid={`view-eir-${e.id}`}
                        title="View EIRs for this container"
                      >
                        <ClipboardList className="w-3.5 h-3.5" /> EIR
                      </Link>
                      <button
                        onClick={() => setEdiEvent(e)}
                        disabled={e.status === "EDI_SENT" || e.status === "CANCELLED"}
                        className="ps-btn ps-btn-secondary h-8 px-3 text-xs"
                        data-testid={`generate-edi-${e.id}`}
                      >
                        <Mail className="w-3.5 h-3.5" /> EDI
                      </button>
                      {canEditEvent && e.status !== "CANCELLED" && (e.status === "EDI_SENT" || e.edi_resend_pending) && (
                        <button
                          onClick={() => resendEdi(e)}
                          className={`ps-btn h-8 px-3 text-xs ${e.edi_resend_pending ? "ps-btn-primary bg-amber-500 hover:bg-amber-600 border-amber-500" : "ps-btn-secondary"}`}
                          data-testid={`resend-edi-${e.id}`}
                          title={e.edi_resend_pending
                            ? "This event was edited after EDI was sent — re-send to sync the carrier's copy"
                            : "Re-send the CODECO message to the carrier"}
                        >
                          <Send className="w-3.5 h-3.5" /> {e.edi_resend_pending ? "Re-send EDI" : "Resend"}
                        </button>
                      )}
                      <button
                        onClick={() => setPhotosEvent(e)}
                        className="ps-btn ps-btn-secondary h-8 px-3 text-xs"
                        data-testid={`view-photos-${e.id}`}
                        title="View attached photos"
                      >
                        <Camera className="w-3.5 h-3.5" /> Photos
                      </button>
                      <button
                        onClick={() => downloadWaybill(e)}
                        className="ps-btn ps-btn-secondary h-8 px-3 text-xs"
                        data-testid={`download-waybill-${e.id}`}
                        title="Download electronic waybill (EIR) PDF"
                      >
                        <Receipt className="w-3.5 h-3.5" /> Waybill
                      </button>
                      {canEditEvent && e.status !== "CANCELLED" && (
                        <button
                          onClick={() => setEditEvent(e)}
                          className="ps-btn ps-btn-secondary h-8 px-3 text-xs"
                          data-testid={`edit-gate-event-${e.id}`}
                          title="Edit booking, ISO type, seal, driver, vessel, etc."
                        >
                          <PenSquare className="w-3.5 h-3.5" /> Edit
                        </button>
                      )}
                      {canCancelEvent && e.status !== "CANCELLED" && (
                        <button
                          onClick={() => cancelEvent(e)}
                          className="ps-btn h-8 px-3 text-xs bg-red-50 hover:bg-red-100 border border-red-200 text-red-700"
                          data-testid={`cancel-gate-event-${e.id}`}
                          title={e.gate_type === "OUT"
                            ? "Reverse this Gate OUT — container returns to IN on the dashboard"
                            : "Cancel this Gate IN — container is removed from the yard"}
                        >
                          <X className="w-3.5 h-3.5" /> {e.gate_type === "OUT" ? "Reverse" : "Cancel"}
                        </button>
                      )}
                      {canCorrectCn && e.status !== "CANCELLED" && (
                        <button
                          onClick={() => setCorrectCnEvent(e)}
                          className="ps-btn ps-btn-secondary h-8 px-3 text-xs"
                          data-testid={`correct-cn-${e.id}`}
                          title="Fix a typo in this container number — cascades across all modules"
                        >
                          <Pencil className="w-3.5 h-3.5" /> Correct number
                        </button>
                      )}
                      {canCorrectCarrier && (
                        <button
                          onClick={() => setCorrectEvent(e)}
                          className="ps-btn ps-btn-secondary h-8 px-3 text-xs"
                          data-testid={`correct-carrier-${e.id}`}
                          title="Correct the carrier recorded on this event (admin)"
                        >
                          <PenSquare className="w-3.5 h-3.5" /> Correct carrier
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <GateEventForm open={showForm} onClose={() => setShowForm(false)} onSaved={onSaved} />
      <EdiDialog event={ediEvent} onClose={() => setEdiEvent(null)} onSent={load} />
      <PhotosDialog event={photosEvent} onClose={() => setPhotosEvent(null)} />
      <CorrectCarrierDialog
        event={correctEvent}
        onClose={() => setCorrectEvent(null)}
        onDone={() => { setCorrectEvent(null); load(); }}
      />
      <EditGateEventDialog
        event={editEvent}
        onClose={() => setEditEvent(null)}
        onDone={() => { setEditEvent(null); load(); }}
      />
      {correctCnEvent && (
        <CorrectContainerNumberDialog
          oldContainerNumber={correctCnEvent.container_number}
          onClose={() => setCorrectCnEvent(null)}
          onDone={() => { setCorrectCnEvent(null); load(); }}
        />
      )}
    </div>
  );
}
